/**
 * AdminCreateUserPage - Create regular (non-test) users
 *
 * Feature: 045-admin-user-tool
 */

import { useState } from 'react';
import { useApolloClient } from '@apollo/client';
import logger from '../../utils/logger';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminCreateUserGraphQL } from '../../hooks/graphql/useAdminMutationsGraphQL';
import { adminUsersUrl, adminUserDetailUrl } from '../../utils/urls';

export function AdminCreateUserPage() {
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    displayName: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const { createUser, loading } = useAdminCreateUserGraphQL();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const result = await createUser({
        username: formData.username,
        email: formData.email,
        displayName: formData.displayName || undefined,
        password: formData.password || undefined
      });

      if (!result) {
        throw new Error('Failed to create user');
      }

      // Refetch admin user queries via Apollo
      await apolloClient.refetchQueries({
        include: ['AdminUsers', 'AdminUserStats'],
      });

      // Show temporary password if one was generated
      if (result.temporaryPassword) {
        alert(
          `User created successfully!\n\nTemporary password: ${result.temporaryPassword}\n\nPlease save this password - it will not be shown again.`
        );
      }

      // Navigate to created user detail page
      navigate(adminUserDetailUrl(result.user.globalId));
    } catch (error: any) {
      logger.error('AdminCreateUserPage', 'Failed to create user:', error);
      alert(error.message || 'Failed to create user');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Create User</h1>
            <p className="text-muted mt-1">Create a new regular user account</p>
          </div>
          <Link
            to={adminUsersUrl()}
            className="px-4 py-2 border border-border rounded-md hover:bg-surface-2 transition-colors"
          >
            Back to Users
          </Link>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-surface-1 rounded-lg border border-border p-6 space-y-6">
          {/* Username */}
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-foreground mb-2">
              Username <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="username"
              required
              minLength={3}
              maxLength={30}
              pattern="[a-zA-Z0-9_-]+"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              className="w-full px-4 py-2 bg-surface border border-border text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="johndoe"
            />
            <p className="text-xs text-muted mt-1">
              3-30 characters, letters, numbers, hyphens, and underscores only
            </p>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 bg-surface border border-border text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="user@example.com"
            />
          </div>

          {/* Display Name */}
          <div>
            <label htmlFor="displayName" className="block text-sm font-medium text-foreground mb-2">
              Display Name
            </label>
            <input
              type="text"
              id="displayName"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              className="w-full px-4 py-2 bg-surface border border-border text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="John Doe"
            />
            <p className="text-xs text-muted mt-1">Optional - defaults to username if not provided</p>
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-2 bg-surface border border-border text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-primary pr-12"
                placeholder="Leave blank to auto-generate"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-foreground"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                    />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                )}
              </button>
            </div>
            <p className="text-xs text-muted mt-1">
              Optional - a temporary password will be auto-generated if left blank
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
            <p className="text-sm text-foreground">
              <strong>Note:</strong> New users will be created with:
            </p>
            <ul className="list-disc list-inside text-sm text-muted mt-2 space-y-1">
              <li>Unverified email status (must verify via email)</li>
              <li>Regular user account (not a test user)</li>
              <li>No initial content (photos, collections, etc.)</li>
              <li>Temporary password shown once if auto-generated</li>
            </ul>
          </div>

          {/* Submit */}
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading || !formData.username || !formData.email}
              className="px-6 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Creating User...
                </span>
              ) : (
                'Create User'
              )}
            </button>

            <Link
              to={adminUsersUrl()}
              className="px-6 py-3 border border-border rounded-md hover:bg-surface-2 transition-colors font-medium flex items-center"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
